import { useEffect, useState } from 'react';
import { ApiError, getStorefront } from './api/client';
import type { StorefrontResponse } from './api/types';
import { TenantShop } from './TenantShop';

function describeError(e: unknown): string {
  return e instanceof ApiError ? `${e.body.code}: ${e.body.description}` : 'Request failed';
}

/**
 * The shop: one selector button per seeded test tenant (a shop window per payment provider),
 * with the selected tenant's catalog + checkout rendered by {@link TenantShop}.
 */
export function ShopPanel() {
  const [storefront, setStorefront] = useState<StorefrontResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    getStorefront()
      .then((s) => {
        setStorefront(s);
        setSelected(s.tenants[0]?.tenantId ?? null);
      })
      .catch((e: unknown) => setError(describeError(e)));
  }, []);

  const selectedTenant = storefront?.tenants.find((t) => t.tenantId === selected) ?? null;

  return (
    <section>
      {error !== null && (
        <p role="alert" style={{ color: '#b00020' }}>
          {error}
        </p>
      )}

      {storefront === null ? (
        error === null ? (
          <p>Loading the storefront…</p>
        ) : null
      ) : (
        <>
          {!storefront.paymentHostConfigured && (
            <p role="alert">
              The payment host relay is not configured in this environment — checkout and the
              provider relay will answer with their not-configured errors.
            </p>
          )}

          <nav style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '1rem 0' }}>
            {storefront.tenants.map((tenant) => (
              <button
                key={tenant.tenantId}
                onClick={() => setSelected(tenant.tenantId)}
                disabled={tenant.tenantId === selected}
                style={{ fontWeight: tenant.tenantId === selected ? 'bold' : 'normal' }}
              >
                {tenant.displayName}{' '}
                <small>
                  — {tenant.provider} · {tenant.checkoutSupported ? 'web checkout' : 'store-billed'}
                </small>
              </button>
            ))}
          </nav>

          {selectedTenant !== null && <TenantShop tenant={selectedTenant} />}
        </>
      )}
    </section>
  );
}
